import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import Chart from 'chart.js';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BankAssuranceService } from 'src/app/_services/bank-assurance.service';

@Component({
  selector: 'app-report-manager',
  templateUrl: './report-manager.component.html',
  styleUrls: ['./report-manager.component.scss']
})
export class ReportManagerComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('countsCanvas') countsCanvas: ElementRef<HTMLCanvasElement> | undefined;
  @ViewChild('caCanvas') caCanvas: ElementRef<HTMLCanvasElement> | undefined;
  @ViewChild('compCountsCanvas') compCountsCanvas: ElementRef<HTMLCanvasElement> | undefined;
  @ViewChild('compCaCanvas') compCaCanvas: ElementRef<HTMLCanvasElement> | undefined;

  public charts: any = {};
  public agencies: any[] = [];
  public selectedAgencies: string[] = [];

  public startDate: string = '';
  public endDate: string = '';
  public isLastTransfer: boolean = true;

  public loading = false;
  public showAgenciesDropdown = false;
  // Indicateur pour les charts (affichage loader) et message si pas de données
  public chartsLoading = false;
  public hasData = false;
  public noDataMessage: string | null = null;
  // `useMockData` removed

  public statusColors = ['#4a90e2', '#7bc043', '#f5a623', '#9b59b6'];
  // Couleurs pour les agences dans les histogrammes (palette étendue)
  public agencyPalette = ['#483D8B', '#7bc043', '#f5a623', '#d9534f', '#5bc0de', '#292b2c'];

  public statusMapping = [
    { key: 'created', short: 'CR', full: 'Créées' },
    { key: 'proposition_to_transfer', short: 'AP', full: 'Approuvées' },
    { key: 'proposition_transfer', short: 'TR', full: 'Transférées' },
    { key: 'paid', short: 'EN', full: 'Encaissées' }
  ];

  public counts: any[] = []; // Pour Doughnut global
  public ca: any[] = [];     // Pour Doughnut global

  // Stockage des données traitées par agence pour les histogrammes
  public agencyGroupedData: any[] = [];

  constructor(private bankService: BankAssuranceService, private router: Router) { }

  ngOnInit(): void {
    this.loadAgenciesForManager();
    const now = new Date();
    this.startDate = `${now.getFullYear()}-01-01`;
    this.endDate = now.toISOString().split('T')[0];
    this.applyFilters();
  }

  // LOGIQUE RÉCUPÉRATION AGENCES CONSERVÉE
  loadAgenciesForManager() {
    const userId = parseInt(localStorage.getItem('ACTIVA_REDACTEUR_BANCASSURANCEUSERID') || '0', 10);
    const token = localStorage.getItem('ACTIVA_REDACTEUR_TOKEN') || '';

    const handleBranchesResp = (res: any) => {
      let arr = Array.isArray(res) ? res : (res.data || res.items || res);
      this.agencies = (arr || []).map((a: any) => ({
        id: a.id || a.branchId || a.bankBranchId,
        name: a.name || a.libelle || a.branchName,
        agencyCode: a.agencyCode || a.code,
        bankId: a.bankId
      }));
      if (this.agencies.length === 0) this.agencies = [{ id: 1, name: 'Agence A', agencyCode: 'A', bankId: 1 }];

      // Sélection par défaut de la première agence et chargement immédiat
      if (this.agencies.length > 0 && this.selectedAgencies.length === 0) {
        this.selectedAgencies = [String(this.agencies[0].id)];
        this.applyFilters();
      }
    };

    if (userId > 0) {
      this.bankService.authenticate(userId).subscribe({
        next: (authRes: any) => {
          const tk = authRes?.token || '';
          this.bankService.getBranchesFromApim(tk || token).subscribe({ next: handleBranchesResp });
        },
        error: () => this.agencies = [{ id: 1, name: 'Agence A', code: 'A' }]
      });
    } else {
      this.bankService.getBranchesFromApim(token).subscribe({ next: handleBranchesResp });
    }
  }

  public toggleAgenciesDropdown() { this.showAgenciesDropdown = !this.showAgenciesDropdown; }
  public isAgencySelected(id: any) { return this.selectedAgencies.includes(String(id)); }
  public toggleAgency(id: any) {
    const key = String(id);
    const idx = this.selectedAgencies.indexOf(key);
    idx === -1 ? this.selectedAgencies.push(key) : this.selectedAgencies.splice(idx, 1);
  }
  public isAllSelected() { return this.agencies.length > 0 && this.selectedAgencies.length === this.agencies.length; }
  public selectAllAgencies(sel: boolean) { this.selectedAgencies = sel ? this.agencies.map(a => String(a.id)) : []; }

  public selectedAgenciesLabel() {
    if (this.selectedAgencies.length === 0) return 'Toutes les agences';
    return this.selectedAgencies.length === 1 ? '1 agence' : `${this.selectedAgencies.length} agences`;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) { if (!(event.target as HTMLElement).closest('.agencies-select-wrapper')) this.showAgenciesDropdown = false; }

  public applyFilters() {
    this.loading = true;
    this.chartsLoading = true;
    this.noDataMessage = null;
    // Éviter l'appel avant que les agences ne soient chargées
    if (this.agencies.length === 0) {
      this.loading = false;
      return;
    }
    // mock mode removed — always call API

    const userId = localStorage.getItem('ACTIVA_REDACTEUR_BANCASSURANCEUSERID') || '0';
    const bankIds = localStorage.getItem('ACTIVA_REDACTEUR_BANkID') 
    // Authentifier d'abord pour obtenir le bon token
    this.bankService.authenticate(parseInt(userId)).subscribe({
      next: (authResponse: any) => {
        if (!authResponse?.token) {
          this.loading = false;
          console.error('No token from authentication');
          return;
        }

        // Construire les bankIds à passer à l'API (dédoublonnés)
        const selectedAgencyIds = this.selectedAgencies.length > 0 ? this.selectedAgencies.map(id => Number(id)) : this.agencies.map(a => a.id);
        console.log('✅ selectedAgencyIds:', selectedAgencyIds);

        const selectedBankIds = this.agencies
          .filter(a => selectedAgencyIds.includes(a.id))
          .map(a => a.bankId)
          .filter((v: any) => v !== undefined && v !== null);
        console.log('✅ selectedBankIds:', selectedBankIds);

        const uniqueBankIds = Array.from(new Set(selectedBankIds));
      //  const bankIds = uniqueBankIds.join(',');
    //   const bankIds = localStorage.getItem('ACTIVA_REDACTEUR_BANkID') 
        console.log('✅ bankIds final:', bankIds);

        // Format de dates pour l'API (DD/MM/YYYY)
        const startDateFormatted = this.formatDateForApi(this.startDate);
        const endDateFormatted = this.formatDateForApi(this.endDate);

        // Pour le manager, agencyCode = liste d'IDs d'agences sélectionnées (séparés par des virgules)
        const agencyCode = this.selectedAgencies.length > 0 ? this.selectedAgencies.join(',') : undefined;

        console.log('🔍 Calling API with:', { startDateFormatted, endDateFormatted, bankIds, agencyCode, selectedAgencyIds });

        // Utiliser le token de l'authentification
        this.bankService.getPoliciesStatistics(startDateFormatted, endDateFormatted, bankIds, authResponse.token, agencyCode, this.isLastTransfer)
          .pipe(catchError((err) => { this.loading = false; this.chartsLoading = false; console.error('Error:', err); return of([]); }))
          .subscribe((res: any) => {
            console.log('📡 RAW API Response:', res);
            // L'API peut retourner un objet ou un tableau - gérer les deux cas
            let data = Array.isArray(res) ? res : (res?.data || res?.items || res?.result || []);
            console.log('📊 Final data to process:', data);
            this.processStatisticsData(data);
          });
      },
      error: (err) => {
        this.loading = false;
        console.error('Authentication error:', err);
      }
    });
  }

  private formatDateForApi(dateStr: string): string {
    if (!dateStr) {
      const today = new Date();
      return today.toISOString().split('T')[0];
    }
    // dateStr est déjà au format YYYY-MM-DD, on le retourne tel quel pour l'API
    return dateStr;
  }

  private processStatisticsData(statisticsArray: any[]) {
    this.loading = false;
    this.chartsLoading = false;

    // Détecter absence de données
    const totalCountsSum = (statisticsArray || []).reduce((s, st) => s + (st.countCreated || 0) + (st.countApproved || 0) + (st.countTransferred || 0) + (st.countPaid || 0), 0);
    this.hasData = (statisticsArray && statisticsArray.length > 0 && totalCountsSum > 0);
    if (!this.hasData) {
      this.noDataMessage = 'Aucune donnée disponible pour la période sélectionnée.';
      this.counts = [];
      this.ca = [];
      this.agencyGroupedData = [];
      this.destroyCharts();
      return;
    }

    // DEBUG: Voir ce que l'API retourne
    console.log('📊 Statistics Array from API:', statisticsArray);
    console.log('🏢 Agencies loaded:', this.agencies);
    console.log('📍 Selected Agencies:', this.selectedAgencies);

    // 1️⃣ DOUGHNUTS GLOBAUX - Agrégation de toutes les agences sélectionnées
    const totalCounts = {
      created: 0,
      approved: 0,
      transferred: 0,
      paid: 0
    };
    const totalCA = {
      created: 0,
      approved: 0,
      transferred: 0,
      paid: 0
    };

    statisticsArray.forEach((stat: any) => {
      totalCounts.created += (stat.countCreated || 0);
      totalCounts.approved += (stat.countApproved || 0);
      totalCounts.transferred += (stat.countTransferred || 0);
      totalCounts.paid += (stat.countPaid || 0);
      // Mapping CA: createdTurnover => created, potentialTurnover => approved, expectedTurnover => transferred
      totalCA.created += (stat.createdTurnover || 0);
      totalCA.approved += (stat.potentialTurnover || 0);
      totalCA.transferred += (stat.expectedTurnover || 0);
      totalCA.paid += (stat.dueTurnover || 0);
    });

    const totalCountsValue = totalCounts.created + totalCounts.approved + totalCounts.transferred + totalCounts.paid || 1;
    this.counts = [
      { label: 'CR', value: totalCounts.created, percent: ((totalCounts.created / totalCountsValue) * 100).toFixed(1) },
      { label: 'AP', value: totalCounts.approved, percent: ((totalCounts.approved / totalCountsValue) * 100).toFixed(1) },
      { label: 'TR', value: totalCounts.transferred, percent: ((totalCounts.transferred / totalCountsValue) * 100).toFixed(1) },
      { label: 'EN', value: totalCounts.paid, percent: ((totalCounts.paid / totalCountsValue) * 100).toFixed(1) }
    ];

    const totalCAValue = totalCA.created + totalCA.approved + totalCA.transferred + totalCA.paid || 1;
    this.ca = [
      { label: 'CR', value: totalCA.created, percent: ((totalCA.created / totalCAValue) * 100).toFixed(1) },
      { label: 'AP', value: totalCA.approved, percent: ((totalCA.approved / totalCAValue) * 100).toFixed(1) },
      { label: 'TR', value: totalCA.transferred, percent: ((totalCA.transferred / totalCAValue) * 100).toFixed(1) },
      { label: 'EN', value: totalCA.paid, percent: ((totalCA.paid / totalCAValue) * 100).toFixed(1) }
    ];

    // 2️⃣ HISTOGRAMMES - Données par agence (groupingKey peut être agencyId ou bankId)
    this.agencyGroupedData = statisticsArray.map((stat: any, index: number) => {
      const groupingId = parseInt(stat.groupingKey);

      // Priorité: chercher une agence par id exact (si l'API renvoie l'agence directement)
      let agencyInfo = this.agencies.find(a => a.id === groupingId);

      // Fallback: si non trouvé, essayer par bankId (comportement précédent)
      if (!agencyInfo) {
        agencyInfo = this.agencies.find(a => a.bankId === groupingId);
      }

      const agencyName = agencyInfo?.name || `Agence ${groupingId}`;
      console.log(`Stat ${index}: groupingId=${groupingId}, found agency:`, agencyInfo?.name || 'NOT FOUND');

      return {
        label: agencyName,
        agencyId: agencyInfo?.id || null,
        bankId: agencyInfo?.bankId || groupingId,
        color: this.agencyPalette[index % this.agencyPalette.length],
        counts: [
          stat.countCreated || 0,
          stat.countApproved || 0,
          stat.countTransferred || 0,
          stat.countPaid || 0
        ],
        ca: [
          stat.createdTurnover || 0,      // CA pour Créées
          stat.potentialTurnover || 0,    // CA pour Approuvées
          stat.expectedTurnover || 0,     // CA pour Transférées
          stat.dueTurnover || 0           // CA pour Payées
        ]
      };
    });

    setTimeout(() => this.updateCharts(), 100);
  }

  // --- GRAPH LOGIC ---
  private formatAbbr(val: any) {
    const n = Number(val);
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
    return n;
  }

  public updateCharts() {
    this.destroyCharts();

    // Plugin Flèches Doughnut
    const calloutPlugin = {
      afterDraw: (chart: any) => {
        const ctx = chart.ctx; ctx.save();
        chart.data.datasets.forEach((dataset: any, i: number) => {
          const meta = chart.getDatasetMeta(i);
          meta.data.forEach((element: any, index: number) => {
            const dataVal = dataset.data[index]; if (dataVal === 0) return;
            const view = element._model;
            const midAngle = view.startAngle + (view.endAngle - view.startAngle) / 2;
            const x1 = view.x + Math.cos(midAngle) * view.outerRadius;
            const y1 = view.y + Math.sin(midAngle) * view.outerRadius;
            const x2 = view.x + Math.cos(midAngle) * (view.outerRadius + 20);
            const y2 = view.y + Math.sin(midAngle) * (view.outerRadius + 20);
            ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
            ctx.strokeStyle = view.backgroundColor; ctx.lineWidth = 2; ctx.stroke();
            const label = chart.data.labels[index];
            const percent = ((dataVal / dataset.data.reduce((a: any, b: any) => a + b, 0)) * 100).toFixed(1);
            const displayVal = chart.canvas.id === 'ca' ? this.formatAbbr(dataVal) : dataVal;
            ctx.font = 'bold 10px sans-serif'; ctx.fillStyle = '#333';
            ctx.textAlign = Math.cos(midAngle) > 0 ? 'left' : 'right';
            ctx.fillText(`${label}: ${displayVal} (${percent}%)`, x2 + (Math.cos(midAngle) > 0 ? 5 : -5), y2);
          });
        });
        ctx.restore();
      }
    };

    const doughnutOpt = {
      responsive: true, maintainAspectRatio: false, cutoutPercentage: 65, legend: { display: false },
      layout: { padding: { left: 50, right: 50, top: 30, bottom: 50 } }
    };

    if (this.countsCanvas) {
      this.charts.counts = new Chart(this.countsCanvas.nativeElement, {
        type: 'doughnut',
        data: { labels: this.counts.map(c => c.label), datasets: [{ data: this.counts.map(c => c.value), backgroundColor: this.statusColors }] },
        options: doughnutOpt, plugins: [calloutPlugin]
      });
      this.charts.counts.canvas.id = 'counts';
    }

    if (this.caCanvas) {
      this.charts.ca = new Chart(this.caCanvas.nativeElement, {
        type: 'doughnut',
        data: { labels: this.ca.map(c => c.label), datasets: [{ data: this.ca.map(c => c.value), backgroundColor: this.statusColors }] },
        options: doughnutOpt, plugins: [calloutPlugin]
      });
      this.charts.ca.canvas.id = 'ca';
    }

    // CONFIGURATION HISTOGRAMMES GROUPÉS
    const barOpt = {
      responsive: true, maintainAspectRatio: false,
      scales: {
        yAxes: [{ gridLines: { display: false }, ticks: { callback: (v: any) => this.formatAbbr(v), beginAtZero: true } }],
        xAxes: [{ gridLines: { display: false } }]
      },
      legend: { position: 'bottom', labels: { boxWidth: 12, fontSize: 11 } },
      // Pour affiner les bandes
      barPercentage: 0.7,
      categoryPercentage: 0.6
    };

    if (this.compCountsCanvas) {
      this.charts.compCounts = new Chart(this.compCountsCanvas.nativeElement, {
        type: 'bar',
        data: {
          labels: this.statusMapping.map(s => s.short),
          datasets: this.agencyGroupedData.map(a => ({
            label: a.label,
            data: a.counts,
            backgroundColor: a.color,
            agencyId: a.agencyId,
            bankId: a.bankId
          }))
        },
        options: barOpt
      });
    }

    if (this.compCaCanvas) {
      this.charts.compCa = new Chart(this.compCaCanvas.nativeElement, {
        type: 'bar',
        data: {
          labels: this.statusMapping.map(s => s.short),
          datasets: this.agencyGroupedData.map(a => ({
            label: a.label,
            data: a.ca,
            backgroundColor: a.color,
            agencyId: a.agencyId,
            bankId: a.bankId
          }))
        },
        options: barOpt
      });
    }
  }

  public handleChartClick(event: MouseEvent, chartKey: string) {
    const chart = this.charts[chartKey];
    if (!chart) return;

    const activePoints = chart.getElementsAtEvent(event);
    if (activePoints.length > 0) {
      const elementIndex = activePoints[0]._index;
      const datasetIndex = activePoints[0]._datasetIndex;

      const statusObj = this.statusMapping[elementIndex];
      const statusKey = statusObj.key;

      // Envoyer les IDs d'agence au lieu des codes
      let targetAgencyIds = this.selectedAgencies.join(',');

      if (chart.config.type === 'bar') {
        // Pour les histogrammes, récupérer l'ID de l'agence cliquée
        // Si une seule agence sélectionnée, on connaît son id
        if (this.selectedAgencies.length === 1) {
          targetAgencyIds = this.selectedAgencies[0];
        } else {
          // Si plusieurs agences, utiliser agencyId si disponible
          const agencyId = chart.data.datasets[datasetIndex].agencyId;
          targetAgencyIds = agencyId ? String(agencyId) : this.selectedAgencies.join(',');
        }
      }

      // Préparer les dates au format attendu par le gestionnaire des polices (DD/MM/YYYY)
      const toTargetDate = (d: string) => {
        if (!d) return d;
        const parts = d.split('-');
        return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d;
      };

      const queryParams: any = {
        status: statusKey,
        startDate: toTargetDate(this.startDate),
        endDate: toTargetDate(this.endDate),
        agencyIds: targetAgencyIds
      };

      if (statusKey === 'proposition_transfer') {
        this.router.navigate(['/welcome/menu/bancassurance/responsable-virements'], { queryParams });
      } else if (statusKey === 'proposition_to_transfer') {
        // AP -> ouvrir directement l'écran "Polices à traiter" (status 'proposition_to_transfer')
        this.router.navigate(['/welcome/menu/bancassurance/responsable-policy-management'], { queryParams });
      } else {
        this.router.navigate(['/welcome/menu/bancassurance/responsable-policy-management'], { queryParams });
      }
    }
  }

  private generateMockPolicies() {
    return [
      { agencyCode: 'A', policyStatus: 'created', totalAmount: 500000 },
      { agencyCode: 'A', policyStatus: 'paid', totalAmount: 1200000 },
      { agencyCode: 'B', policyStatus: 'created', totalAmount: 800000 },
      { agencyCode: 'B', policyStatus: 'paid', totalAmount: 300000 },
      { agencyCode: 'B', policyStatus: 'proposition_to_transfer', totalAmount: 450000 }
    ];
  }

  private generateMockStatistics() {
    return [
      {
        customerID: 1,
        countCreated: 5,
        countApproved: 8,
        countTransferred: 3,
        countPaid: 2,
        potentialTurnover: 45000,
        expectedTurnover: 120000,
        dueTurnover: 15000
      },
      {
        customerID: 2,
        countCreated: 12,
        countApproved: 6,
        countTransferred: 9,
        countPaid: 4,
        potentialTurnover: 75000,
        expectedTurnover: 200000,
        dueTurnover: 30000
      }
    ];
  }

  private destroyCharts() { Object.keys(this.charts).forEach(k => { if (this.charts[k]) this.charts[k].destroy(); }); }
  ngAfterViewInit() { this.updateCharts(); }
  ngOnDestroy() { this.destroyCharts(); }
}
